/**
 * MADE-065: BlockedCard
 * Variant of KanbanCard for ProductionOrder blocked orchestrators.
 * Shows block reason and outstanding child job IDs.
 * Schema stub only — no dispatch logic yet (MADE-067/068).
 */

import React, { useState } from 'react';
import { Paper, Typography, Stack, Chip, Alert, Button, Box, Collapse } from '@mui/material';
import { Block as BlockIcon, Inventory as InventoryIcon } from '@mui/icons-material';
import { authFetch } from '../../utils/authFetch';
import type { WorkOrder } from '../../types/madebb.types';

interface BlockedCardProps {
  workOrder: WorkOrder;
  onViewDetail: (wo: WorkOrder) => void;
}

interface BomMaterial {
  name: string;
  required: number;
  available: number;
}

export const BlockedCard: React.FC<BlockedCardProps> = ({ workOrder, onViewDetail }) => {
  const [bomResults, setBomResults] = useState<BomMaterial[] | null>(null);
  const [bomLoading, setBomLoading] = useState(false);
  const [bomOpen, setBomOpen] = useState(false);

  const handleCheckBom = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBomLoading(true);
    try {
      const res = await authFetch(`/api/madebb/manufacturing-orders/${workOrder.id}/bom-status`);
      const data = res.ok ? await res.json() : { materials: [] };
      setBomResults(data.materials ?? []);
      setBomOpen(true);
    } catch {
      setBomResults([]);
      setBomOpen(true);
    } finally {
      setBomLoading(false);
    }
  };

  return (
  <Paper
    elevation={2}
    sx={{
      p: 1.5,
      borderRadius: 2,
      borderLeft: '4px solid #d32f2f',
      cursor: 'pointer',
      '&:hover': { bgcolor: 'grey.50' },
    }}
    onClick={() => onViewDetail(workOrder)}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
      <Typography variant="subtitle2" fontWeight={700} noWrap>
        {workOrder.orderNumber}
      </Typography>
      <Chip
        label="Production"
        size="small"
        sx={{ fontSize: 10, height: 20, bgcolor: '#1565c0', color: 'white' }}
      />
    </Stack>

    <Typography variant="body2" noWrap color="text.secondary" mb={0.5}>
      {workOrder.description}
    </Typography>

    {workOrder.customerName && (
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {workOrder.customerName}
      </Typography>
    )}

    {/* Block reason */}
    <Alert
      severity="error"
      icon={<BlockIcon fontSize="small" />}
      sx={{ py: 0.5, mb: 0.5 }}
    >
      <Typography variant="caption" fontWeight={700}>
        Blocked on {workOrder.blockedOn === 'procurement' ? 'Procurement' : 'Production'}
      </Typography>
    </Alert>

    {/* Outstanding child ManufacturingOrder IDs */}
    {workOrder.blockedChildJobs && workOrder.blockedChildJobs.length > 0 && (
      <Stack spacing={0.25} mb={0.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Outstanding jobs ({workOrder.blockedChildJobs.length}):
        </Typography>
        {workOrder.blockedChildJobs.map(jobId => (
          <Typography key={jobId} variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            • {jobId}
          </Typography>
        ))}
      </Stack>
    )}

    {/* Procurement blockers */}
    {workOrder.procurementBlockers && workOrder.procurementBlockers.length > 0 && (
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          <InventoryIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
          Awaiting procurement:
        </Typography>
        {workOrder.procurementBlockers.map(b => (
          <Typography key={b.materialId} variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            • {b.materialName} × {b.quantity}
          </Typography>
        ))}
      </Stack>
    )}

    {/* BOM Issue badge */}
    <Chip
      label="BOM Issue"
      color="error"
      size="small"
      data-testid="bom-issue-badge"
      sx={{ mt: 0.5, mb: 0.5 }}
    />

    {/* Check BOM button */}
    <Button
      size="small"
      variant="outlined"
      color="error"
      data-testid="check-bom-btn"
      disabled={bomLoading}
      onClick={handleCheckBom}
      sx={{ mt: 0.5, fontSize: 11, mr: 1 }}
    >
      {bomLoading ? 'Loading…' : 'Check BOM'}
    </Button>

    <Button
      size="small"
      variant="text"
      sx={{ mt: 0.5, fontSize: 11, p: 0 }}
      onClick={e => { e.stopPropagation(); onViewDetail(workOrder); }}
    >
      View details
    </Button>

    {/* BOM status results */}
    <Collapse in={bomOpen}>
      <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }} data-testid="bom-results">
        {bomResults && bomResults.length === 0 && (
          <Typography variant="caption" color="text.secondary">No missing materials found.</Typography>
        )}
        {bomResults && bomResults.map((m, i) => (
          <Typography key={i} variant="caption" display="block" color="error.main">
            • {m.name}: need {m.required}, have {m.available}
          </Typography>
        ))}
      </Box>
    </Collapse>
  </Paper>
  );
};
